import { useParams } from "react-router-dom";
import { useClientScope } from "@/components/pages/Client/settings/ClientScopeContext";
import { useAppSelector } from "@/redux/hooks";
import { clientBookingCountriesSelector, operatorBookingCountriesSelector } from "@/redux/slices/operator/selectors";

/**
 * The countries the booking sheet may place a trip in, as lower-case ISO codes — applied to the typed
 * address search, the saved favourites/recents/provider POIs it offers, the pin-on-map result, and the
 * airport-terminal lookup that follows a flight pickup (DEV-1606, DCS-367).
 *
 * - An operator booking on behalf of a client — the Clients → Book route, which carries `:operatorId` and a
 *   managed-client scope — gets the provider's bookable list, `bookable_country_list` (the domain's
 *   `d-operates-in-countries`, AU and NZ for Royale). Adding a country there is how a provider lets its
 *   staff book further afield.
 * - Everyone else (self-service client, guest widget, EA on behalf) gets the provider's base country only.
 *
 * Never worldwide: each tier falls back to the other's source when its own is empty, and if the provider has
 * neither the list is empty and nothing is offered. Same operator test as PricedBookingSummary's
 * `isOperatorScoped`, so the price override and the wider search are granted to the same person. Nothing
 * server-side reads this: the quote engine's country whitelist and the admin-only transfer-type gate still
 * apply to whatever address gets through.
 */
export const useAddressSearchCountries = (): string[] => {
  const { accountId } = useClientScope();
  const { operatorId } = useParams<{ operatorId: string }>();
  const operatorCountries = useAppSelector(operatorBookingCountriesSelector);
  const clientCountries = useAppSelector(clientBookingCountriesSelector);
  return accountId && operatorId ? operatorCountries : clientCountries;
};
